/**
 * Profile Detail — pure model.
 *
 * The other-user profile-detail surface loads a single profile by id
 * and renders Friend or Sponsor variant presentation. The Message CTA
 * uses the canonical direct-chat entry `/chat/open/:targetProfileId`
 * (Bucket C symmetric). Both Sponsor-viewing-Friend and
 * Friend-viewing-Sponsor now reach the same canonical channel row.
 *
 * R1+R2 (correction ACT): ProfileDetail now carries the owner's
 * gender and goal so the public role label is rendered from the
 * OWNER's fields, not the viewer's. Per partner-corrections.md
 * PC-005 / PC-007 and canonical-product-contract.md CONTRACT-001.3.
 *
 * CORRECTION03 (B-2.B): ProfileDetail now also carries `photos` so
 * the view can render the durable server-canonical profile image.
 * The transport is the role-neutral
 * `/api/v1/profile-photos/{photoID}/content` (single source of
 * truth: `frontend/src/api/profilePhotos.ts`). The view must NEVER
 * reconstruct role-prefixed content URLs.
 */

import type { ProfilePhoto } from '@/api/profilePhotos';

export type ProfileDetailStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type ProfileVariant = 'friend' | 'sponsor';

export type ProfileDetailGender = 'male' | 'female';

export type ProfileDetailGoal =
  | 'equal_seeker'
  | 'wealthy_seeker'
  | 'no_work'
  | 'companion_seeker';

export interface ProfileDetail {
  readonly id: string;
  readonly profile_type: 'friend' | 'sponsor';
  /** Owner's self-gender (per CONTRACT-002). Drives the role label. */
  readonly gender?: ProfileDetailGender;
  /** Owner's goal (per CONTRACT-003). Drives the role label. */
  readonly goal?: ProfileDetailGoal;
  readonly display_name?: string;
  readonly headline?: string;
  readonly bio?: string;
  readonly city_or_region?: string;
  readonly updated_at: string;
  /**
   * CORRECTION03: durable server-canonical photo list. Empty array
   * is canonical "no photos" (server returns []). The view renders
   * the first-by-position image when present, or NoPhotoFallback
   * when empty. The FE MUST NOT fabricate / reconstruct photo URLs.
   */
  readonly photos: readonly ProfilePhoto[];
}

export interface ProfileDetailModel {
  readonly status: ProfileDetailStatus;
  readonly profileId: string;
  readonly variant: ProfileVariant;
  readonly detail: ProfileDetail | null;
  readonly messageCtaError: string | null;
  readonly messageCtaLoading: boolean;
  /** Whether the CTA is supported for the current viewer-kind x variant. */
  readonly messageCtaEnabled: boolean;
  /**
   * CORRECTION03: protocol violations from the photo normalizer.
   * Surfaced loudly (visible warning) per the single-source-of-truth
   * contract; the valid photos are still adopted.
   */
  readonly photoProtocolViolations: readonly string[];
}

export function initialDetailModel(
  profileId: string,
  variant: ProfileVariant
): ProfileDetailModel {
  return {
    status: 'idle',
    profileId,
    variant,
    detail: null,
    messageCtaError: null,
    messageCtaLoading: false,
    // R3 (correction ACT): default to disabled; the harness sets the
    // correct value once it knows the viewer's registrationKind.
    // The view layer is the single source of truth for "is CTA
    // visible".
    messageCtaEnabled: false,
    photoProtocolViolations: [],
  };
}